/**
 * Pinia Library Processor
 *
 * Handles Pinia store composables for Vue 3:
 * - useXxxStore: Access Pinia store (state, getters, actions)
 * - storeToRefs: Convert store state to reactive refs
 *
 * This processor creates:
 * - External Entity Input nodes for store state properties
 * - External Entity Input nodes for store getters (with type "computed")
 * - Process nodes for store action calls
 * - Data flows for storeToRefs conversions
 *
 * Example:
 * ```vue
 * const store = useCounterStore();
 * const { count, doubleCount } = storeToRefs(store);
 * store.increment();
 * ```
 *
 * Results in:
 * - External Entity Input node for useCounterStore
 * - Data flows from store to local refs (count, doubleCount)
 * - Process node for increment action
 */
import { looksLikeAction } from './helpers.js';
/**
 * Pinia library processor
 * Handles Pinia store composables for Vue 3
 */
export class PiniaLibraryProcessor {
    metadata = {
        id: 'pinia',
        libraryName: 'pinia',
        packagePatterns: ['pinia'],
        hookNames: [/^use\w+Store$/, 'storeToRefs'],
        priority: 95, // Higher than Vue core processor to handle Pinia stores specifically
        description: 'Pinia state management library processor for Vue 3',
        mergeable: false // Each store instance should be separate
    };
    /**
     * Check if this processor should handle the given hook
     */
    shouldHandle(hook, context) {
        const { logger } = context;
        // Check if hook name matches the pattern (useXxxStore or storeToRefs)
        const matchesPattern = this.metadata.hookNames.some(pattern => {
            if (pattern instanceof RegExp) {
                return pattern.test(hook.hookName);
            }
            return pattern === hook.hookName;
        });
        if (!matchesPattern) {
            logger.debug(`Hook ${hook.hookName} does not match Pinia pattern`);
            return false;
        }
        // Accept hooks with state-management category or no category
        // This allows Pinia stores to be detected even when categorized by the composables analyzer
        if (hook.category && hook.category !== 'state-management' && hook.category !== 'state') {
            logger.debug(`Hook ${hook.hookName} has incompatible category ${hook.category}, skipping Pinia processor`);
            return false;
        }
        logger.debug(`Hook ${hook.hookName} will be handled by Pinia processor`);
        return true;
    }
    /**
     * Process the hook and return DFD elements
     */
    process(hook, context) {
        const { logger } = context;
        logger.start(hook.hookName, hook);
        // Delegate to specific handler based on hook name
        if (hook.hookName === 'storeToRefs') {
            return this.processStoreToRefs(hook, context);
        }
        else if (/^use\w+Store$/.test(hook.hookName)) {
            return this.processUseStore(hook, context);
        }
        logger.warn(`Unknown Pinia composable: ${hook.hookName}`);
        return { nodes: [], edges: [], handled: false };
    }
    /**
     * Process useXxxStore composable
     * Creates an external entity input node for store access
     */
    processUseStore(hook, context) {
        const { generateNodeId, logger } = context;
        const nodes = [];
        const edges = [];
        // Separate data properties (state/getters) from action properties
        const dataProperties = [];
        const actionProperties = [];
        const propertyMetadata = {};
        // Classify variables using heuristic-based approach
        // State and getters are data, actions are processes
        for (const variable of hook.variables) {
            if (looksLikeAction(variable)) {
                actionProperties.push(variable);
                propertyMetadata[variable] = {
                    dfdElementType: 'process',
                    isAction: true
                };
            }
            else {
                dataProperties.push(variable);
                propertyMetadata[variable] = {
                    dfdElementType: 'external-entity-input',
                    isStateOrGetter: true
                };
            }
        }
        logger.debug(`Data properties (state/getters): ${dataProperties.join(', ')}`);
        logger.debug(`Action properties: ${actionProperties.join(', ')}`);
        // Create consolidated library-hook node for the store (data-store for Pinia stores)
        const nodeId = generateNodeId('library_hook');
        const node = {
            id: nodeId,
            label: hook.hookName,
            type: 'data-store',
            line: hook.line,
            column: hook.column,
            metadata: {
                category: 'library-hook',
                hookName: hook.hookName,
                libraryName: 'pinia',
                isLibraryHook: true,
                isPiniaStore: true,
                properties: [...dataProperties, ...actionProperties],
                dataProperties,
                processProperties: actionProperties,
                propertyMetadata,
                line: hook.line,
                column: hook.column
            }
        };
        nodes.push(node);
        logger.node('created', node);
        logger.complete({ nodes, edges, handled: true });
        return { nodes, edges, handled: true };
    }
    /**
     * Process storeToRefs composable
     * storeToRefs is a utility that extracts refs from a store, but we don't need to visualize it
     * as a separate node. The store properties will be displayed directly from the store node.
     */
    processStoreToRefs(hook, context) {
        const { logger } = context;
        const nodes = [];
        const edges = [];
        // storeToRefs is a utility function that doesn't need visualization
        // The properties extracted from storeToRefs will be displayed directly from the store node
        logger.debug('storeToRefs detected but not creating nodes (properties will be displayed from store)');
        logger.complete({ nodes, edges, handled: true });
        return { nodes, edges, handled: true };
    }
}
//# sourceMappingURL=pinia.js.map